"use client"

import { useEffect, useState } from "react"
import { Button } from "../../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import { Edit, Plus, Trash, ChevronDown, ChevronUp } from "lucide-react"
import { Toast } from "../../../components/Toast"
import { getWeeklyLevels, createWeeklyLevel, deleteWeeklyLevel } from "../../../api/adminService"

const ITEMS_PER_PAGE = 5
const QUESTION_TYPES = ["multiple-choice", "true-false", "short-answer"]

interface Question {
  id: string
  type: string
  question: string
  options?: string[]
  correctAnswer: string
  explanation: string
}

interface LevelForm {
  id?: string
  title: string
  description: string
  week_number: string
  is_active: boolean
  questions: Question[]
}

export function WeeklyGameAdminSection({levels, setLevels}:{levels: any[], setLevels:any}) {
//   const [levels, setLevels] = useState<any[]>([])
  const [expanded, setExpanded] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)

  // Main dialog state
  const [mainDialogOpen, setMainDialogOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [levelForm, setLevelForm] = useState<LevelForm>({
    title: "",
    description: "",
    week_number: "",
    is_active: false,
    questions: [],
  })

  // Question dialog state
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false)
  const [editingQuestionIndex, setEditingQuestionIndex] = useState<number | null>(null)
  const [questionForm, setQuestionForm] = useState<Question>({
    id: "",
    type: "multiple-choice",
    question: "",
    options: ["", "", "", ""],
    correctAnswer: "",
    explanation: "",
  })

  const totalPages = Math.ceil(levels.length / ITEMS_PER_PAGE)
  const paginatedLevels = levels.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  const fetchLevels = async () => {
    try {
      const data = await getWeeklyLevels()
      setLevels(data)
    } catch (err) {
      Toast.error("Failed to fetch levels")
    }
  }

  useEffect(() => {
    fetchLevels()
  }, [])

  const toggleExpand = (id: string) => {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
  }

  const handleNewLevel = () => {
    setIsEditMode(false)
    setLevelForm({
      title: "",
      description: "",
      week_number: "",
      is_active: false,
      questions: [],
    })
    setMainDialogOpen(true)
  }

  const handleEditLevel = (level: any) => {
    setIsEditMode(true)
    setLevelForm({
      id: level.id,
      title: level.title,
      description: level.description,
      week_number: level.week_number?.toString() || "",
      is_active: level.is_active || false,
      questions: level.questions || [],
    })
    setMainDialogOpen(true)
  }

  const handleOpenQuestionDialog = (index?: number) => {
    if (index !== undefined) {
      // Edit existing question
      setEditingQuestionIndex(index)
      const q = levelForm.questions[index]
      setQuestionForm({
        id: q.id || "",
        type: q.type || "multiple-choice",
        question: q.question || "",
        options: q.options || ["", "", "", ""],
        correctAnswer: q.correctAnswer || "",
        explanation: q.explanation || "",
      })
    } else {
      // Add new question
      setEditingQuestionIndex(null)
      setQuestionForm({
        id: `q-${Date.now()}`,
        type: "multiple-choice",
        question: "",
        options: ["", "", "", ""],
        correctAnswer: "",
        explanation: "",
      })
    }
    setQuestionDialogOpen(true)
  }

  const handleSaveQuestion = () => {
    if (!questionForm.question.trim()) {
      Toast.error("Please enter a question")
      return
    }

    if (questionForm.type === "multiple-choice") {
      if (questionForm.options?.some((o) => !o.trim())) {
        Toast.error("Please fill in all options")
        return
      }
      if (!questionForm.correctAnswer) {
        Toast.error("Please select the correct answer")
        return
      }
    } else if (questionForm.type === "true-false") {
      if (!questionForm.correctAnswer) {
        Toast.error("Please select the correct answer")
        return
      }
    } else if (questionForm.type === "short-answer") {
      if (!questionForm.correctAnswer.trim()) {
        Toast.error("Please enter the correct answer")
        return
      }
    }

    const updatedQuestions = [...levelForm.questions]
    if (editingQuestionIndex !== null) {
      updatedQuestions[editingQuestionIndex] = questionForm
    } else {
      updatedQuestions.push(questionForm)
    }

    setLevelForm({ ...levelForm, questions: updatedQuestions })
    setQuestionDialogOpen(false)
    // Toast.success(editingQuestionIndex !== null ? "Question updated" : "Question added")
  }

  const handleDeleteQuestion = (index: number) => {
    const updatedQuestions = levelForm.questions.filter((_, i) => i !== index)
    setLevelForm({ ...levelForm, questions: updatedQuestions })
    Toast.success("Question removed")
  }

  const handleSubmitLevel = async () => {
    if (!levelForm.title.trim()) {
      Toast.error("Please enter a title")
      return
    }
    if (!levelForm.week_number) {
      Toast.error("Please enter a week number")
      return
    }
    if (levelForm.questions.length === 0) {
      Toast.error("Please add at least one question")
      return
    }

    try {
      const payload = {
        ...(isEditMode && { id: levelForm.id }),
        title: levelForm.title,
        description: levelForm.description,
        week_number: Number.parseInt(levelForm.week_number),
        is_active: levelForm.is_active,
        questions: levelForm.questions,
      }

      const result = await createWeeklyLevel(payload)

      if (!result) {
        Toast.error("Operation failed. Check console for details.")
        return
      }

      if (isEditMode) {
        setLevels((prev:any) => prev.map((l:any) => (l.id === levelForm.id ? payload : l)))
        Toast.success("Level updated successfully")
      } else {
        setLevels((prev:any) => [payload, ...prev])
        Toast.success("Level created successfully")
      }

      setMainDialogOpen(false)
    } catch (err) {
      Toast.error("Operation failed: " + err)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteWeeklyLevel(id)
      setLevels((prev:any) => prev.filter((l:any) => l.id !== id))
      Toast.success("Level deleted successfully")
    } catch (err) {
      Toast.error("Delete failed: " + err)
    }
  }

  const renderQuestionPreview = (q: Question) => {
    return (
      <div className="border rounded p-3 bg-muted/5 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold bg-primary/10 text-primary px-2 py-1 rounded">{q.type}</span>
            </div>
            <p className="font-medium text-sm">{q.question}</p>
          </div>
        </div>

        {q.type === "multiple-choice" && q.options && (
          <ul className="space-y-1 ml-2">
            {q.options.map((opt, i) => (
              <li
                key={i}
                className={`text-sm ${
                  opt === q.correctAnswer ? "text-green-600 font-semibold" : "text-muted-foreground"
                }`}
              >
                {opt === q.correctAnswer && "✓ "} {opt}
              </li>
            ))}
          </ul>
        )}

        {q.type === "true-false" && (
          <ul className="space-y-1 ml-2">
            {["True", "False"].map((opt) => (
              <li
                key={opt}
                className={`text-sm ${
                  opt === q.correctAnswer ? "text-green-600 font-semibold" : "text-muted-foreground"
                }`}
              >
                {opt === q.correctAnswer && "✓ "} {opt}
              </li>
            ))}
          </ul>
        )}

        {q.type === "short-answer" && (
          <p className="text-sm ml-2">
            <span className="text-green-600 font-semibold">Answer:</span> {q.correctAnswer}
          </p>
        )}

        {q.explanation && (
          <p className="text-xs text-muted-foreground italic border-t pt-2">
            <strong>Explanation:</strong> {q.explanation}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">🎮 Weekly Game Levels</CardTitle>
              <CardDescription>Manage all weekly game levels, their questions, and player activity.</CardDescription>
            </div>

            <Button onClick={handleNewLevel}>
              <Plus className="h-4 w-4 mr-2" /> New Level
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {paginatedLevels.map((lvl) => (
            <div key={lvl.id} className="border rounded p-4 bg-muted/10 space-y-2 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold">
                    Week {lvl.week_number || "?"}: {lvl.title || "Untitled"}
                  </h4>
                  <p className="text-sm text-muted-foreground">{lvl.description || "No description"}</p>
                </div>

                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => toggleExpand(lvl.id)}>
                    {expanded.includes(lvl.id) ? (
                      <>
                        Collapse <ChevronUp className="ml-1 h-4 w-4" />
                      </>
                    ) : (
                      <>
                        Expand <ChevronDown className="ml-1 h-4 w-4" />
                      </>
                    )}
                  </Button>

                  <Button variant="ghost" size="sm" onClick={() => handleEditLevel(lvl)}>
                    <Edit className="h-4 w-4" />
                  </Button>

                  <Button variant="ghost" size="sm" onClick={() => handleDelete(lvl.id)}>
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {expanded.includes(lvl.id) && (
                <div className="mt-3 space-y-3 text-sm">
                  <p className="text-muted-foreground">
                    <strong>Players:</strong> {lvl.player_count || 0}
                  </p>
                  {lvl.questions?.length ? (
                    <div className="space-y-2">
                      <p className="font-medium">Questions ({lvl.questions.length})</p>
                      {lvl.questions.map((q: Question, i: number) => (
                        <div key={i}>{renderQuestionPreview(q)}</div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No questions added</p>
                  )}
                </div>
              )}
            </div>
          ))}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4">
            <p className="text-sm text-muted-foreground">
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, levels.length)}{" "}
              of {levels.length}
            </p>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                First
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </Button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1
                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                )
              })}
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(totalPages)}
              >
                Last
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Level Dialog */}
      <Dialog open={mainDialogOpen} onOpenChange={setMainDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEditMode ? "Edit Level" : "Create New Level"}</DialogTitle>
            <DialogDescription>
              {isEditMode
                ? "Update level details and manage questions."
                : "Define details and questions for this week's level."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Basic Info Section */}
            <div className="space-y-3 pb-4 border-b">
              <h3 className="font-semibold text-sm">Basic Information</h3>

              <div>
                <label className="text-sm font-medium">Title</label>
                <Input
                  value={levelForm.title}
                  onChange={(e:any) => setLevelForm({ ...levelForm, title: e.target.value })}
                  placeholder="e.g., Bible Trivia Week 1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={levelForm.description}
                  onChange={(e:any) => setLevelForm({ ...levelForm, description: e.target.value })}
                  placeholder="Describe this level..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Week Number</label>
                  <Input
                    type="number"
                    placeholder="e.g., 1"
                    value={levelForm.week_number}
                    onChange={(e:any) => setLevelForm({ ...levelForm, week_number: e.target.value })}
                  />
                </div>

                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={levelForm.is_active}
                      onChange={(e) => setLevelForm({ ...levelForm, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">Active</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Questions Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm">Questions ({levelForm.questions.length})</h3>
                <Button size="sm" onClick={() => handleOpenQuestionDialog()}>
                  <Plus className="h-4 w-4 mr-1" /> Add Question
                </Button>
              </div>

              {levelForm.questions.length > 0 ? (
                <div className="space-y-2">
                  {levelForm.questions.map((q, i) => (
                    <div key={i} className="border w-full max-w-[620px] rounded p-3 bg-muted/5 flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold bg-primary/10 text-primary px-2 py-1 rounded">
                            Q{i + 1}
                          </span>
                          <span className="text-xs bg-secondary/20 text-secondary-foreground px-2 py-1 rounded">
                            {q.type}
                          </span>
                        </div>
                        <p className="text-sm font-medium truncate">{q.question}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenQuestionDialog(i)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteQuestion(i)}>
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No questions added yet. Click "Add Question" to get started.
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMainDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmitLevel}>{isEditMode ? "Update Level" : "Create Level"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestionIndex !== null ? "Edit Question" : "Add Question"}</DialogTitle>
            <DialogDescription>Select a question type and fill in the details.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Question Type Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Question Type</label>
              <select
                className="w-full border rounded p-2 text-sm"
                value={questionForm.type}
                onChange={(e) => {
                  const newType = e.target.value
                  setQuestionForm({
                    ...questionForm,
                    type: newType,
                    options: newType === "true-false" ? ["True", "False"] : ["", "", "", ""],
                    correctAnswer: "",
                  })
                }}
              >
                {QUESTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.replace("-", " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>

            {/* Question Text */}
            <div>
              <label className="text-sm font-medium mb-2 block">Question</label>
              <Textarea
                value={questionForm.question}
                onChange={(e:any) => setQuestionForm({ ...questionForm, question: e.target.value })}
                placeholder="Enter the question text..."
                rows={3}
              />
            </div>

            {/* Multiple Choice Options */}
            {questionForm.type === "multiple-choice" && (
              <div>
                <label className="text-sm font-medium mb-2 block">Options</label>
                <div className="grid grid-cols-2 gap-2">
                  {questionForm.options?.map((opt, i) => (
                    <Input
                      key={i}
                      placeholder={`Option ${i + 1}`}
                      value={opt}
                      onChange={(e:any) => {
                        const opts = [...(questionForm.options || [])]
                        opts[i] = e.target.value
                        setQuestionForm({ ...questionForm, options: opts })
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Correct Answer Selection */}
            <div>
              <label className="text-sm font-medium mb-2 block">Correct Answer</label>
              {questionForm.type === "multiple-choice" && (
                <select
                  className="w-full border rounded p-2 text-sm"
                  value={questionForm.correctAnswer}
                  onChange={(e) => setQuestionForm({ ...questionForm, correctAnswer: e.target.value })}
                >
                  <option value="">Select correct answer</option>
                  {questionForm.options?.map(
                    (opt, i) =>
                      opt && (
                        <option key={i} value={opt}>
                          {opt}
                        </option>
                      ),
                  )}
                </select>
              )}

              {questionForm.type === "true-false" && (
                <select
                  className="w-full border rounded p-2 text-sm"
                  value={questionForm.correctAnswer}
                  onChange={(e) => setQuestionForm({ ...questionForm, correctAnswer: e.target.value })}
                >
                  <option value="">Select correct answer</option>
                  <option value="True">True</option>
                  <option value="False">False</option>
                </select>
              )}

              {questionForm.type === "short-answer" && (
                <Input
                  value={questionForm.correctAnswer}
                  onChange={(e:any) => setQuestionForm({ ...questionForm, correctAnswer: e.target.value })}
                  placeholder="Enter the correct answer..."
                />
              )}
            </div>

            {/* Explanation */}
            <div>
              <label className="text-sm font-medium mb-2 block">Explanation (Optional)</label>
              <Textarea
                value={questionForm.explanation}
                onChange={(e:any) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                placeholder="Provide an explanation for the correct answer..."
                rows={2}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setQuestionDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveQuestion}>
              {editingQuestionIndex !== null ? "Update Question" : "Add Question"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
